import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import QRCode from 'qrcode'
import { BuyerSmmFulfillment } from '#bv/components/customer/BuyerSmmFulfillment'
import { DiscordIcon } from '#bv/components/DiscordIcon'
import {
  hasBuyerSmmOrder,
  smmCryptoProgressMessage,
  type BuyerSmmStatusFields,
} from '#bv/lib/buyerSmmStatus'
import {
  blockExplorerTxUrl,
  cryptoReceiveProgress,
  formatCryptoAmount,
} from '#bv/lib/cryptoPaymentDisplay'
import type { NowPaymentPublic } from '#bv/lib/nowpayments'
import { getCryptoLedgerSummary, getNowPaymentsPayment } from '#bv/lib/nowpayments'

type LedgerSummary = {
  items: { productId: number; name: string; unitPrice: number; quantity: number }[]
  buyerEmail: string
  totalUsd: number
  internalOrderId: string
} | null

export const Route = createFileRoute('/checkout/crypto/$paymentId')({
  component: CryptoCheckoutPage,
})

const TERMINAL = new Set(['finished', 'failed', 'expired', 'refunded'])

function statusPhase(status: string): 'pay' | 'network' | 'done' {
  const s = status.toLowerCase()
  if (s === 'finished' || s === 'paid' || s === 'overpaid' || s === 'complete' || s === 'completed') return 'done'
  if (s === 'confirming' || s === 'sending' || s === 'confirmed' || s === 'partial') return 'network'
  return 'pay'
}

function paymentStatusLabel(payment: NowPaymentPublic): string {
  const s = payment.paymentStatus.toLowerCase()
  if (s === 'finished') return 'Confirmed'
  if (s === 'confirming') {
    if (payment.transactions.some((t) => t.status === 'unconfirmed')) return 'Confirming…'
    if (
      payment.expectedCrypto != null &&
      payment.receivedCrypto != null &&
      payment.receivedCrypto > 0 &&
      payment.receivedCrypto < payment.expectedCrypto * 0.999
    ) {
      return 'Partial payment'
    }
    return 'Confirming…'
  }
  if (s === 'waiting') {
    if (payment.receivedCrypto != null && payment.receivedCrypto > 0) return 'Payment detected'
    return 'Waiting for payment'
  }
  return payment.paymentStatus
}

function CryptoPaymentProgress({ payment }: { payment: NowPaymentPublic }) {
  const cur = payment.payCurrency.toUpperCase()
  const expected = payment.expectedCrypto ?? payment.payAmount
  const received = payment.receivedCrypto
  const { pct: amountPct, label: amountLabel } = cryptoReceiveProgress(received, expected)
  const confReq = payment.confirmationsRequired
  const confCur = payment.confirmationsCurrent
  const confPct = confReq > 0 ? Math.min(100, Math.round((confCur / confReq) * 100)) : 0
  const hasReceived = received != null && received > 0
  const hasTxs = payment.transactions.length > 0
  const done = payment.paymentStatus === 'finished'

  if (!hasReceived && !hasTxs && !done) return null

  const showConfirmations = (hasTxs || hasReceived) && !done

  return (
    <div
      className="mb-8 rounded-2xl border p-5 sm:p-6"
      style={{ borderColor: 'rgba(0, 191, 255,0.2)', background: 'rgba(255,255,255,0.03)' }}
    >
      <h2 className="font-display text-lg tracking-wide text-[#f0f6ff]">Payment progress</h2>

      <ProgressBlock
        label="Amount received"
        detail={`${amountLabel} ${cur}`}
        pct={done ? 100 : amountPct}
        tone={done ? 'emerald' : amountPct >= 100 ? 'violet' : 'cyan'}
      />

      {showConfirmations ? (
        <ProgressBlock
          label="Network confirmations"
          detail={`${confCur} of ${confReq} confirmations`}
          pct={confPct}
          tone="violet"
        />
      ) : done && confReq > 0 ? (
        <ProgressBlock
          label="Network confirmations"
          detail={`${confReq} of ${confReq} confirmations`}
          pct={100}
          tone="emerald"
        />
      ) : null}

      {hasTxs ? <TxList payment={payment} cur={cur} /> : null}
    </div>
  )
}

function ProgressBlock({
  label,
  detail,
  pct,
  tone,
}: {
  label: string
  detail: string
  pct: number
  tone: 'cyan' | 'violet' | 'emerald'
}) {
  const bar =
    tone === 'emerald'
      ? 'bg-emerald-400'
      : tone === 'violet'
        ? 'bg-violet-400'
        : 'bg-gradient-to-r from-[#00bfff] to-[#7b2fbe]'
  return (
    <div className="mt-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span className="font-medium text-[#a8c4e8]">{label}</span>
        <span className="font-mono text-[#f0f6ff]">{detail}</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
        <div className={`h-full rounded-full transition-all duration-500 ${bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function TxList({ payment, cur }: { payment: NowPaymentPublic; cur: string }) {
  return (
    <div className="mt-6 border-t border-white/10 pt-5">
      <p className="text-xs font-medium uppercase tracking-wider text-[#5a6d8a]">Transactions</p>
      <ul className="mt-3 space-y-3">
        {payment.transactions.map((tx) => {
          const explorer = blockExplorerTxUrl(payment.payCurrency, tx.txid)
          const confirmed = tx.status === 'confirmed'
          return (
            <li
              key={tx.txid}
              className="rounded-xl border border-white/10 bg-black/20 px-3 py-3 text-sm sm:px-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    confirmed
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : 'bg-amber-500/15 text-amber-200'
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${confirmed ? 'bg-emerald-400' : 'animate-pulse bg-amber-400'}`}
                  />
                  {confirmed ? 'Confirmed' : 'Unconfirmed'}
                </span>
                {tx.amountCrypto != null ? (
                  <span className="font-mono text-xs text-[#a8c4e8]">
                    {formatCryptoAmount(tx.amountCrypto)} {cur}
                  </span>
                ) : null}
              </div>
              <p className="mt-2 break-all font-mono text-xs text-[#8ecfff]">{tx.txid}</p>
              {explorer ? (
                <a
                  href={explorer}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-xs font-medium text-[#a5d8ff] hover:underline"
                >
                  View on block explorer →
                </a>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function qrPayloadForPayment(payment: NowPaymentPublic): string {
  const address = payment.payAddress?.trim()
  if (!address) return ''
  const amount = payment.payAmount != null ? String(payment.payAmount) : ''
  const c = (payment.payCurrency ?? '').trim().toLowerCase()
  if (c === 'btc') return amount ? `bitcoin:${address}?amount=${encodeURIComponent(amount)}` : `bitcoin:${address}`
  if (c === 'ltc') return amount ? `litecoin:${address}?amount=${encodeURIComponent(amount)}` : `litecoin:${address}`
  if (c === 'eth') return amount ? `ethereum:${address}?value=${encodeURIComponent(amount)}` : `ethereum:${address}`
  if (c === 'trx' || c === 'trx-usdt' || c === 'usdttrc20') return address
  return amount ? `${address}|${amount}|${c}` : address
}

function CryptoCheckoutPage() {
  const { paymentId } = Route.useParams()
  const [payment, setPayment] = useState<NowPaymentPublic | null>(null)
  const [ledger, setLedger] = useState<LedgerSummary>(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState<'addr' | 'inv' | 'amt' | ''>('')
  const [buyerSmm, setBuyerSmm] = useState<BuyerSmmStatusFields | null>(null)

  const load = useCallback(async () => {
    setError('')
    try {
      const p = await getNowPaymentsPayment({ data: { paymentId } })
      setPayment(p)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load payment.')
    }
  }, [paymentId])

  useEffect(() => {
    void load()
    const t = window.setInterval(() => void load(), 4000)
    return () => window.clearInterval(t)
  }, [load])

  useEffect(() => {
    void getCryptoLedgerSummary({ data: { paymentId } })
      .then((row) => setLedger(row))
      .catch(() => setLedger(null))
  }, [paymentId])

  const phase = payment ? statusPhase(payment.paymentStatus) : 'pay'
  const done = statusPhase(payment?.paymentStatus ?? '') === 'done'

  useEffect(() => {
    const email = ledger?.buyerEmail?.trim()
    if (!done || !email) {
      setBuyerSmm(null)
      return
    }
    const poll = async () => {
      try {
        const qs = new URLSearchParams({ orderId: paymentId, email })
        const res = await fetch(`/api/buyer-order-status?${qs.toString()}`)
        if (!res.ok) return
        const data = (await res.json()) as BuyerSmmStatusFields & { smmStatus?: string }
        const fields: BuyerSmmStatusFields = {
          smmStatus: data.smmStatus,
          smmOrderId: data.smmOrderId,
          smmTiktokTarget: data.smmTiktokTarget,
          smmPackageLabel: data.smmPackageLabel,
          smmQuantity: data.smmQuantity,
          smmPlatform: data.smmPlatform,
          smmMetric: data.smmMetric,
        }
        setBuyerSmm(hasBuyerSmmOrder(fields) ? fields : null)
      } catch {
        /* ignore */
      }
    }
    void poll()
    const t = window.setInterval(() => void poll(), 5000)
    return () => window.clearInterval(t)
  }, [done, paymentId, ledger?.buyerEmail])
  const stepIndex = !payment ? 0 : done ? 2 : 1
  const qrPayload = payment ? qrPayloadForPayment(payment) : ''
  const [qrSrc, setQrSrc] = useState<string>('')

  useEffect(() => {
    let cancelled = false
    if (!qrPayload) {
      setQrSrc('')
      return
    }
    void QRCode.toDataURL(qrPayload, {
      width: 220,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#0b0810', light: '#ffffff' },
    })
      .then((dataUrl) => {
        if (!cancelled) setQrSrc(dataUrl)
      })
      .catch(() => {
        if (!cancelled) setQrSrc('')
      })
    return () => {
      cancelled = true
    }
  }, [qrPayload])

  const copy = async (text: string, kind: 'addr' | 'inv' | 'amt') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(kind)
      window.setTimeout(() => setCopied(''), 2000)
    } catch {
      setError('Could not copy. Select and copy manually.')
    }
  }

  const steps = useMemo(
    () => [
      { id: 'info', label: 'Order information' },
      { id: 'pay', label: 'Confirm & pay' },
      { id: 'receive', label: 'Receive your items' },
    ],
    [],
  )

  const networkBannerText = useMemo(() => {
    if (!payment || phase !== 'network') return ''
    if (payment.transactions.some((t) => t.status === 'unconfirmed')) {
      return 'Payment detected — waiting for blockchain confirmations…'
    }
    const exp = payment.expectedCrypto ?? payment.payAmount
    const rec = payment.receivedCrypto
    if (exp != null && rec != null && rec > 0 && rec < exp * 0.999) {
      return 'Partial payment received — please send the remaining amount.'
    }
    return 'Confirming your payment…'
  }, [payment, phase])

  const detectedWhileWaiting =
    payment &&
    !done &&
    phase === 'pay' &&
    ((payment.receivedCrypto != null && payment.receivedCrypto > 0) || payment.transactions.length > 0)

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(165deg, #080a12 0%, #050508 45%, #0c0e18 100%)' }}>
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col lg:flex-row">
        {/* Left — order summary */}
        <aside
          className="flex w-full flex-col border-b p-6 sm:p-10 lg:w-[380px] lg:border-b-0 lg:border-r"
          style={{ borderColor: 'rgba(0,191,255,0.12)', background: 'rgba(255,255,255,0.02)' }}
        >
          <div className="mb-8 flex items-center gap-3">
            <img src="/pulsedigital-logo-mark.png" alt="" className="h-11 w-auto object-contain" style={{ filter: 'drop-shadow(0 0 8px rgba(0, 191, 255,0.35)) drop-shadow(0 0 12px rgba(123, 47, 190,0.25))' }} />
            <span className="font-display text-xl tracking-widest chrome-text">PulseDigital</span>
          </div>
          <p className="text-xs font-medium uppercase tracking-[0.2em]" style={{ color: '#5a6d8a' }}>
            Total due
          </p>
          <p className="mt-1 font-display text-4xl tracking-wide text-[#f0f6ff] sm:text-5xl">
            {(() => {
              const usd =
                ledger?.totalUsd ??
                payment?.priceAmount ??
                null
              return usd != null ? (
                <>
                  ${Number(usd).toFixed(2)}{' '}
                  <span className="text-lg font-normal tracking-normal text-[#a8c4e8]">USD</span>
                </>
              ) : (
                '—'
              )
            })()}
          </p>
          {ledger?.buyerEmail ? (
            <p className="mt-3 text-xs" style={{ color: '#5a6d8a' }}>
              Checkout email: <span className="text-[#8ecfff]">{ledger.buyerEmail}</span>
            </p>
          ) : null}
          <p className="mt-2 text-xs" style={{ color: '#5a6d8a' }}>
            Order ID:{' '}
            <span className="break-all font-mono text-[#8ecfff]" title="Use this in support / Discord">
              {paymentId}
            </span>
          </p>
          <div className="mt-10 max-h-64 space-y-3 overflow-y-auto border-t border-white/10 pt-8">
            {ledger?.items?.length ? (
              ledger.items.map((line) => (
                <div key={`${line.productId}-${line.name}`} className="flex justify-between gap-3 text-sm">
                  <span className="min-w-0 flex-1 truncate text-[#a8c4e8]" title={line.name}>
                    {line.name} <span className="text-[#5a6d8a]">×{line.quantity}</span>
                  </span>
                  <span className="shrink-0 font-medium text-[#f0f6ff]">
                    ${(line.unitPrice * line.quantity).toFixed(2)}
                  </span>
                </div>
              ))
            ) : (
              <div className="flex justify-between gap-4 text-sm">
                <span className="truncate text-[#a8c4e8]" title={payment?.orderDescription}>
                  {payment?.orderDescription?.split('—')[0]?.trim() || 'Order'}
                </span>
                <span className="shrink-0 font-medium text-[#f0f6ff]">
                  {payment ? `$${payment.priceAmount.toFixed(2)}` : '—'}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t border-white/10 pt-4 text-sm font-semibold text-[#a5d8ff]">
              <span>Total</span>
              <span>
                {ledger?.totalUsd != null
                  ? `$${ledger.totalUsd.toFixed(2)}`
                  : payment
                    ? `$${payment.priceAmount.toFixed(2)}`
                    : '—'}
              </span>
            </div>
          </div>
          <div className="mt-auto flex flex-wrap gap-4 pt-12">
            <a
              href="https://discord.gg/tGxhDfRkdE"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
              aria-label="Discord"
            >
              <DiscordIcon white={false} size={18} style={{ color: '#5865F2' }} />
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-4 gap-y-2 text-xs" style={{ color: '#5a6d8a' }}>
            <Link to="/terms" className="hover:text-[#5a6d8a]">
              Terms
            </Link>
            <Link to="/refund" className="hover:text-[#5a6d8a]">
              Refunds
            </Link>
            <Link to="/contact" className="hover:text-[#5a6d8a]">
              Contact
            </Link>
          </div>
        </aside>

        {/* Right — payment flow */}
        <main className="flex-1 px-4 py-8 sm:px-8 sm:py-12">
          <nav className="mb-10 flex border-b border-white/10 text-sm" aria-label="Checkout steps">
            {steps.map((step, i) => (
              <div
                key={step.id}
                className={`flex-1 pb-3 text-center font-medium transition-colors ${
                  i === stepIndex ? 'border-b-2 border-[#00bfff] text-[#f0f6ff]' : i < stepIndex ? 'text-[#a8c4e8]' : 'text-[#5a6d8a]'
                }`}
                style={{ marginBottom: '-1px' }}
              >
                {step.label}
              </div>
            ))}
          </nav>

          {error ? (
            <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>
          ) : null}

          {phase === 'network' && payment ? (
            <div
              className="mb-6 flex items-center gap-3 rounded-xl border px-4 py-3 text-sm"
              style={{
                borderColor: 'rgba(168,85,247,0.35)',
                background: 'rgba(168,85,247,0.08)',
                color: '#e9d5ff',
              }}
            >
              <span className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-violet-400 border-t-transparent" />
              {networkBannerText}
            </div>
          ) : null}

          {detectedWhileWaiting ? (
            <div
              className="mb-6 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm"
              style={{ borderColor: 'rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)', color: '#fde68a' }}
            >
              <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-amber-400" />
              Payment detected on the network. Waiting for confirmations…
            </div>
          ) : null}

          {payment && !done && phase === 'pay' && !detectedWhileWaiting ? (
            <div
              className="mb-6 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm"
              style={{ borderColor: 'rgba(34,197,94,0.25)', background: 'rgba(34,197,94,0.06)', color: '#bbf7d0' }}
            >
              <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
              Waiting for payment. This page updates automatically.
            </div>
          ) : null}

          {done ? (
            <div className="mb-8 rounded-2xl border border-emerald-500/25 bg-emerald-500/10 p-6 text-center">
              <p className="font-display text-xl tracking-wide text-emerald-200">Payment received</p>
              <p className="mt-2 text-sm text-emerald-100/90">
                {buyerSmm
                  ? smmCryptoProgressMessage(buyerSmm.smmPlatform, buyerSmm.smmMetric)
                  : "Thank you — we'll fulfill your order shortly."}
              </p>
              {buyerSmm ? (
                <div className="mt-5 text-left">
                  <BuyerSmmFulfillment
                    {...buyerSmm}
                    orderId={paymentId}
                    buyerEmail={ledger?.buyerEmail}
                    compact
                  />
                  {ledger?.buyerEmail ? (
                    <p className="mt-3 text-center text-xs text-emerald-100/80">
                      <Link
                        to="/customer/orders/$orderId"
                        params={{ orderId: paymentId }}
                        search={{ email: ledger.buyerEmail }}
                        className="font-semibold text-[#a5d8ff] hover:underline"
                      >
                        Track anytime in My orders
                      </Link>
                    </p>
                  ) : null}
                </div>
              ) : null}
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to={
                    ledger?.items?.some((i) => i.productId === 999999 || i.name.toLowerCase().includes('donation'))
                      ? '/donation/thanks'
                      : '/checkout/success'
                  }
                  search={
                    ledger?.items?.some((i) => i.productId === 999999 || i.name.toLowerCase().includes('donation'))
                      ? { paymentId }
                      : { crypto: '1', paymentId }
                  }
                  className="btn-primary inline-flex rounded-xl px-6 py-2.5 text-sm font-semibold"
                >
                  {ledger?.items?.some((i) => i.productId === 999999 || i.name.toLowerCase().includes('donation'))
                    ? 'Donation complete'
                    : 'Order complete'}
                </Link>
                <Link to="/shop" className="btn-chrome inline-flex rounded-xl px-6 py-2.5 text-sm font-semibold">
                  Continue shopping
                </Link>
              </div>
            </div>
          ) : null}

          {payment ? <CryptoPaymentProgress payment={payment} /> : null}

          {payment?.payAddress ? (
            <div className="mb-8 rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:p-5">
              {qrSrc ? (
                <div className="mb-4 inline-flex rounded-xl border border-white/10 bg-black/30 p-2">
                  <img
                    src={qrSrc}
                    alt="Payment QR code"
                    width={220}
                    height={220}
                    className="h-[180px] w-[180px] rounded-md sm:h-[220px] sm:w-[220px]"
                  />
                </div>
              ) : null}
              <p className="text-xs font-medium uppercase tracking-wider text-[#5a6d8a]">Send exactly</p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <p className="font-mono text-lg text-[#f0f6ff]">
                  {payment.expectedCrypto != null || payment.payAmount != null
                    ? formatCryptoAmount(payment.expectedCrypto ?? payment.payAmount ?? 0)
                    : '—'}{' '}
                  {payment.payCurrency.toUpperCase()}
                </p>
                {payment.payAmount != null || payment.expectedCrypto != null ? (
                  <button
                    type="button"
                    onClick={() => void copy(String(payment.expectedCrypto ?? payment.payAmount), 'amt')}
                    className="rounded-lg border border-white/15 px-2 py-1 text-xs text-[#a5d8ff] hover:bg-white/5"
                  >
                    {copied === 'amt' ? 'Copied' : 'Copy'}
                  </button>
                ) : null}
              </div>
              <p className="mt-3 text-xs text-[#5a6d8a]">To address</p>
              <div className="mt-1 flex flex-wrap items-center gap-2 break-all font-mono text-sm text-[#8ecfff]">
                {payment.payAddress}
                <button
                  type="button"
                  onClick={() => void copy(payment.payAddress, 'addr')}
                  className="shrink-0 rounded-lg border border-white/15 px-2 py-1 text-xs text-[#a5d8ff] hover:bg-white/5"
                >
                  {copied === 'addr' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          ) : null}

          {payment ? (
            <div className="overflow-hidden rounded-xl border border-white/10">
              <table className="w-full text-left text-sm">
                <tbody className="divide-y divide-white/10">
                  <tr>
                    <th className="px-4 py-3 font-medium text-[#5a6d8a]">Invoice ID</th>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-[#8ecfff] sm:text-sm">{payment.paymentId}</span>
                      <button
                        type="button"
                        onClick={() => void copy(payment.paymentId, 'inv')}
                        className="ml-2 rounded-lg border border-white/15 px-2 py-0.5 text-xs text-[#a5d8ff] hover:bg-white/5"
                      >
                        {copied === 'inv' ? 'Copied' : 'Copy'}
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <th className="px-4 py-3 font-medium text-[#5a6d8a]">Payment method</th>
                    <td className="px-4 py-3 font-medium capitalize text-[#f0f6ff]">{payment.payCurrency || '—'}</td>
                  </tr>
                  <tr>
                    <th className="px-4 py-3 font-medium text-[#5a6d8a]">Total (fiat)</th>
                    <td className="px-4 py-3 text-[#8ecfff]">
                      ${payment.priceAmount.toFixed(2)} {payment.priceCurrency.toUpperCase()}
                    </td>
                  </tr>
                  <tr>
                    <th className="px-4 py-3 font-medium text-[#5a6d8a]">Expected crypto</th>
                    <td className="px-4 py-3 font-mono text-[#f0f6ff]">
                      {payment.expectedCrypto != null || payment.payAmount != null
                        ? `${formatCryptoAmount(payment.expectedCrypto ?? payment.payAmount ?? 0)} ${payment.payCurrency.toUpperCase()}`
                        : '—'}
                    </td>
                  </tr>
                  {payment.receivedCrypto != null && payment.receivedCrypto > 0 ? (
                    <tr>
                      <th className="px-4 py-3 font-medium text-[#5a6d8a]">Received crypto</th>
                      <td className="px-4 py-3 font-mono text-emerald-200">
                        {formatCryptoAmount(payment.receivedCrypto)} {payment.payCurrency.toUpperCase()}
                      </td>
                    </tr>
                  ) : null}
                  <tr>
                    <th className="px-4 py-3 font-medium text-[#5a6d8a]">Status</th>
                    <td className="px-4 py-3 text-[#a5d8ff]">{paymentStatusLabel(payment)}</td>
                  </tr>
                  {payment.createdAt ? (
                    <tr>
                      <th className="px-4 py-3 font-medium text-[#5a6d8a]">Created</th>
                      <td className="px-4 py-3 text-[#a8c4e8]">{payment.createdAt}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : !error ? (
            <div className="flex items-center gap-3 text-[#5a6d8a]">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#00bfff] border-t-transparent" />
              Loading payment…
            </div>
          ) : null}

          <p className="mt-8 text-xs leading-relaxed" style={{ color: '#5a6d8a' }}>
            Crypto payments are processed securely. Send the exact amount shown; underpayments may require manual review.
          </p>
        </main>
      </div>
    </div>
  )
}
